import React, { useState } from 'react';
import { Dialog, DialogTrigger, DialogContent, DialogFooter, TextArea, Button, Text, useToast } from '@chatsdet/ui';
import { useAuth } from '../../contexts/AuthContext';
import { ApiError } from '../../lib/api-error';
import {
  useFeedbackRepos,
  useSubmitFeedback,
  useUploadFeedbackScreenshot,
  feedbackScreenshotRawUrl,
} from './api';

type FeedbackType = 'bug' | 'feature';

interface FeedbackReportModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  initialType: FeedbackType;
  screenshotDataUrl: string | null;
}

export function FeedbackReportModal({
  isOpen,
  onOpenChange,
  initialType,
  screenshotDataUrl,
}: FeedbackReportModalProps) {
  const [type, setType] = useState<FeedbackType>(initialType);
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { user, organizations } = useAuth();
  const submitFeedback = useSubmitFeedback();
  const uploadScreenshot = useUploadFeedbackScreenshot();
  const { data: reposData } = useFeedbackRepos({ enabled: isOpen });
  const { toast } = useToast();

  // Reset form when modal opens with new type
  React.useEffect(() => {
    if (isOpen) {
      setType(initialType);
      setDescription('');
    }
  }, [isOpen, initialType]);

  const repos = reposData?.data ?? [];
  const defaultRepo = repos[0];
  const orgName =
    organizations?.find((o) => o.id === user?.orgId)?.name ?? user?.orgId ?? 'unknown';

  const handleSubmit = async () => {
    if (!description.trim() || !defaultRepo) return;

    setIsSubmitting(true);
    // Tracks which network call we were on when an error was thrown, so the
    // toast can be specific instead of a generic "failed to submit".
    let phase: 'upload-screenshot' | 'submit' = 'submit';
    try {
      // Pre-upload screenshot to the task-files S3 path. Intentionally NOT
      // routed through the org attachments table — task files are owned by
      // the task and must never surface in /test-data/attachments.
      let screenshotMarkdown = '';
      let screenshot:
        | { storageKey: string; filename: string; contentType: string; sizeBytes: number }
        | undefined;
      if (screenshotDataUrl) {
        phase = 'upload-screenshot';
        const blob = dataUrlToBlob(screenshotDataUrl);
        const file = new File([blob], `feedback-screenshot-${Date.now()}.png`, {
          type: 'image/png',
        });
        const uploadResult = await uploadScreenshot.mutateAsync(file);
        if (uploadResult.data) {
          screenshot = uploadResult.data;
          screenshotMarkdown = `\n\n### Screenshot\n![Screenshot](${feedbackScreenshotRawUrl(uploadResult.data.storageKey)})`;
        }
      }
      phase = 'submit';

      // Build title from first line
      const firstLine = description.trim().split('\n')[0];
      const title = firstLine.length > 200 ? firstLine.slice(0, 197) + '...' : firstLine;

      // Build full description
      const typeLabel = type === 'bug' ? 'Bug Report' : 'Feature Request';
      const fullDescription = [
        `## ${typeLabel}`,
        '',
        description.trim(),
        screenshotMarkdown,
        '',
        '---',
        `**URL:** ${window.location.href}`,
        `**Date:** ${new Date().toISOString()}`,
        `**Reporter:** ${user?.name ?? 'Unknown'} (${user?.email ?? 'no email'})`,
        `**Organization:** ${orgName} (${user?.orgId})`,
      ].join('\n');

      await submitFeedback.mutateAsync({
        propertyGithubRepoId: defaultRepo.id,
        title,
        description: fullDescription,
        triggerType: 'immediate',
        tags: ['user-reported', type === 'bug' ? 'bug' : 'feature-request'],
        priority: type === 'bug' ? 1 : 0,
        screenshot,
      });

      toast.success(type === 'bug' ? 'Bug report submitted' : 'Feature request submitted');
      onOpenChange(false);
    } catch (err) {
      // Sentry capture is wired in the global MutationCache onError handler
      // (apps/web/src/main.tsx) — both feedback mutations have mutationKey
      // set so they group correctly. Here we just surface a useful toast.
      const message =
        err instanceof ApiError
          ? err.message
          : phase === 'upload-screenshot'
            ? 'Failed to upload screenshot. Please try again.'
            : 'Failed to submit report. Please try again.';
      toast.error(message, { reportError: false });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
      <span style={{ display: 'none' }} />
      <Dialog title={type === 'bug' ? 'Report Bug' : 'Request Feature'}>
        <DialogContent>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setType('bug')}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                type === 'bug'
                  ? 'bg-destructive text-destructive-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-accent'
              }`}
            >
              Bug
            </button>
            <button
              type="button"
              onClick={() => setType('feature')}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                type === 'feature'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-accent'
              }`}
            >
              Feature
            </button>
          </div>

          <TextArea
            label="Description"
            placeholder={
              type === 'bug'
                ? 'Describe what went wrong and what you expected to happen...'
                : 'Describe the feature you would like and why...'
            }
            value={description}
            onChange={(v) => setDescription(v)}
            rows={5}
          />

          {screenshotDataUrl && (
            <div>
              <Text size="sm" className="text-muted-foreground mb-1">
                Screenshot preview
              </Text>
              <img
                src={screenshotDataUrl}
                alt="Screenshot preview"
                className="max-h-32 rounded border border-border object-contain"
              />
            </div>
          )}

          {!defaultRepo && (
            <Text size="sm" className="text-destructive">
              No repository configured — cannot submit feedback.
            </Text>
          )}
        </DialogContent>

        <DialogFooter>
          <Button variant="outline" onPress={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant={type === 'bug' ? 'destructive' : 'primary'}
            onPress={handleSubmit}
            isDisabled={!description.trim() || !defaultRepo || isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}

function dataUrlToBlob(dataUrl: string): Blob {
  const match = /^data:([^;,]+)?(;base64)?,([\s\S]*)$/.exec(dataUrl);
  if (!match) {
    throw new Error('Invalid screenshot data URL');
  }

  const contentType = match[1] || 'application/octet-stream';
  const isBase64 = match[2] === ';base64';
  const payload = match[3] ?? '';
  const binary = isBase64 ? atob(payload) : decodeURIComponent(payload);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type: contentType });
}
